import { ref } from 'vue';
import type { HealthIssue, ScreenSummary } from '@codot/contracts';
import { healthApi } from '../api/health.api';

/**
 * One Health Check issue open in its side panel, from anywhere in the CMS (a player's content badge, …). The panels
 * themselves are mounted once in the app shell (IssuePanelHost), the same ones the Health Check list opens.
 */
export const openIssue = ref<HealthIssue | null>(null);

/** Which Health Check problems each content badge stands for, most specific first. */
const FOR_STATUS: Partial<Record<NonNullable<ScreenSummary['content']>['status'], string[]>> = {
  failing: ['PLAYER_CANT_DOWNLOAD', 'PLAYER_CONTENT_REJECTED', 'PLAYER_OUT_OF_SYNC'],
  problem: ['PLAYER_FILES_FAILED', 'PLAYER_CANT_DOWNLOAD', 'PLAYER_OUT_OF_SYNC'],
  offline: ['PLAYER_OFFLINE', 'PLAYER_CANT_DOWNLOAD', 'PLAYER_OUT_OF_SYNC'],
  waiting: ['PLAYER_OUT_OF_SYNC', 'PLAYER_CANT_DOWNLOAD'],
  updating: ['PLAYER_OUT_OF_SYNC'],
};
export const badgeHasIssue = (status: string | undefined): boolean => !!status && status in FOR_STATUS;

/** Open the player's issue that explains its badge. False: Health Check has none open for it (yet). */
export async function openPlayerIssue(screenId: string, status: string): Promise<boolean> {
  const codes = FOR_STATUS[status as keyof typeof FOR_STATUS] ?? [];
  const { items } = await healthApi.issues({ state: 'open', screenId, limit: 50 });
  // An offline player's file and download problems aren't evaluated (its reports are stale): "offline" explains it.
  const hit = [...codes, 'PLAYER_OFFLINE'].map((c) => items.find((i) => i.code === c)).find(Boolean);
  if (!hit) return false;
  openIssue.value = hit;
  return true;
}
